import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { GlobalWords } from "~/lib/global.words";
import {
  FeatureFlag,
  features,
  getVisibleFeatureFlags,
  setFeatureFlag,
  checkFeatureEnabled,
  toggleFeatureFlag,
} from "~/login/features";

interface FeatureFlagsContextType {
  features: FeatureFlag[];
  isFeatureEnabled: (name: FeatureFlag["name"]) => boolean;
  toggleFeature: (name: FeatureFlag["name"]) => void;
  setFeature: (name: FeatureFlag["name"], enabled: boolean) => void;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}
const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(
  undefined,
);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [availableFeatures, setAvailableFeatures] =
    useState<FeatureFlag[]>(features);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Allow user to open the feature flags modal by typing "flags"
  useEffect(() => {
    GlobalWords.register("flags", async () => {
      setIsModalOpen(true);
    });
  }, []);

  const isFeatureEnabled = (name: FeatureFlag["name"]) => {
    return checkFeatureEnabled(name);
  };

  const setFeature = (name: FeatureFlag["name"], enabled: boolean) => {
    setFeatureFlag(name, enabled);
    setAvailableFeatures([...features]); // re-render
  };

  const toggleFeature = (name: FeatureFlag["name"]) => {
    toggleFeatureFlag(name);
    setAvailableFeatures([...features]); // re-render
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const value = {
    features: getVisibleFeatureFlags(availableFeatures),
    isFeatureEnabled,
    setFeature,
    toggleFeature,
    isModalOpen,
    closeModal,
    openModal,
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider",
    );
  }
  return context;
}
